/**
 * AppToolbar — the ONE shared form action bar (CLAUDE.md §18.6).  Every form's
 * toolbar renders through here so the buttons appear in the SAME canonical order
 * with the SAME theme-orange style on every form.  Forms only choose which
 * buttons show / are enabled (via props) and supply any form-specific buttons
 * (the `left` slot) — they never reorder or restyle.
 *
 * CANONICAL ORDER (left → right):
 *   [ form-specific buttons (left slot) ] · New · Edit · Delete ·
 *   [ `beforeRefresh` slot ] · Refresh · [Import · Template] · Export to
 *
 * The reload action is always labelled **Refresh** (never "Retrieve").
 * All buttons use the shared `.ca-btn` (theme-orange) style.  Layout (compact
 * uniform spacing, one control height, group dividers) lives in the co-located
 * AppToolbar.css so it applies on every form automatically.
 *
 * NOTE (BO UI Standard v2 ruling, 2026-07-01): Audit Trail is VIEW-ONLY and is
 * NOT a toolbar action — it renders as a quiet chip on the TITLE ROW via
 * `<FormTitle auditFormName=… auditSourceId=…>`, never here.
 */
import { useState, type ReactNode } from 'react';
import { BusyIcon } from '../loading';
import { ExportMenu } from './ExportMenu';
import './AppToolbar.css';

/** One option in the "Export to" dropdown (ALPACA-style multi-format export). */
export interface ExportOption { label: string; icon?: string; onSelect: () => void; }

/** Data for the shared full-format "Export to" (CSV / HTML / Image / MHT / Text /
 *  PDF / RTF / XLS / XLSX / DOCX).  Pass the form's FULL filtered dataset (built
 *  with the shared `gridExportMatrix(filtered, columns)`) so ALL rows export, not
 *  just the visible page.  Takes precedence over `exportOptions` / `onExport`. */
export interface ExportData {
  filename: string; headers: string[]; rows: string[][];
  /** Optional async source for ALL rows — used by server-paginated lists
   *  (ModuleListView) that must fetch every page for the export.  When set, the
   *  static `headers`/`rows` seed the visible-page fallback and this supplies the
   *  full dataset at export time. */
  rowsProvider?: () => Promise<{ headers: string[]; rows: string[][] }>;
}

export interface AppToolbarProps {
  /** Form-specific buttons rendered to the LEFT of New (e.g. Save, Save As,
   *  Query, Validate, Approve, Deliver).  Use shared `.ca-btn` buttons. */
  left?: ReactNode;

  /** New / Add. */
  onNew?: () => void;
  newLabel?: string;
  newDisabled?: boolean;

  /** Edit the selected row. */
  onEdit?: () => void;
  editDisabled?: boolean;

  /** Delete the selected row(s). */
  onDelete?: () => void;
  deleteDisabled?: boolean;

  /** Preview the selected record (e.g. render its PDF).  Rendered after Delete,
   *  before Refresh.  Opt-in — only shows when provided. */
  onPreview?: () => void;
  previewDisabled?: boolean;
  previewLabel?: string;

  /** Reload data — ALWAYS labelled "Refresh". */
  onRefresh?: () => void;
  refreshing?: boolean;
  refreshDisabled?: boolean;

  /** Import (bulk CSV upload) — shown ONLY for lookups that support bulk load
   *  (BO UI Standard §6).  Rendered after Refresh, before Export. */
  onImport?: () => void;
  importDisabled?: boolean;
  /** Template download (blank CSV of the columns) — paired with Import. */
  onTemplate?: () => void;

  /** Export to (CSV / XLSX / …) — the form owns the actual export/menu. */
  onExport?: () => void;
  exportLabel?: string;
  exportDisabled?: boolean;
  /** When given, "Export to" becomes a dropdown of these formats (ALPACA parity).
   *  Takes precedence over `onExport`. */
  exportOptions?: ExportOption[];
  /** SHARED full-format "Export to" (CSV / HTML / Image / MHT / Text / PDF / RTF /
   *  XLS / XLSX / DOCX) that exports ALL the rows you pass.  This is the standard
   *  export for every list form — build it with `gridExportMatrix(filtered, cols)`.
   *  Takes precedence over `exportOptions` and `onExport`. */
  exportData?: ExportData;

  /** Content rendered between Delete and Refresh (e.g. Date Filter on Sample
   *  Registration).  Separated by dividers automatically. */
  beforeRefresh?: ReactNode;

  /** Content rendered AFTER Refresh (and Import/Template) and BEFORE Export — for
   *  a form-specific action that belongs next to Export, e.g. a "Reports ▾"
   *  dropdown.  Opt-in; renders nothing when unset. */
  beforeExport?: ReactNode;

  /** Extra content rendered at the very RIGHT (rare — e.g. a load-time chip). */
  right?: ReactNode;
}

export function AppToolbar({
  left,
  onNew, newLabel = 'New', newDisabled,
  onEdit, editDisabled,
  onDelete, deleteDisabled,
  onPreview, previewDisabled, previewLabel = 'Preview',
  onRefresh, refreshing, refreshDisabled,
  onImport, importDisabled, onTemplate,
  beforeRefresh,
  beforeExport,
  onExport, exportLabel = 'Export to', exportDisabled, exportOptions, exportData,
  right,
}: AppToolbarProps) {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  return (
    <div className="dp-title-actions app-toolbar">
      {left}
      {/* Button glyphs are the EXACT BO UI Standard prototype characters
          (docs/prototypes/Settings-BO-Method-Example.html .tbtn .i spans). */}
      {/* New = NEUTRAL button with a GREEN ＋ (BO UI Standard §7).  Orange is
          reserved for the ONE primary/active action (Save) — a solid-orange New
          would put two orange buttons on a detail bar (New + Save). */}
      {onNew && (
        <button className="ca-btn" onClick={onNew} disabled={newDisabled} title={newLabel}>
          <span className="tb-i tb-i-green">＋</span> {newLabel}
        </button>
      )}
      {onEdit && (
        <button className="ca-btn" onClick={onEdit} disabled={editDisabled} title="Edit">
          <span className="tb-i tb-i-blue">✎</span> Edit
        </button>
      )}
      {onDelete && (
        <button className="ca-btn" onClick={onDelete} disabled={deleteDisabled} title="Delete">
          <span className="tb-i tb-i-red">🗑</span> Delete
        </button>
      )}
      {onPreview && (
        <button className="ca-btn" onClick={onPreview} disabled={previewDisabled} title={previewLabel}>
          <span className="tb-i tb-i-blue">👁</span> {previewLabel}
        </button>
      )}
      {beforeRefresh}
      {onRefresh && (
        <button className="ca-btn" onClick={onRefresh} disabled={refreshDisabled || refreshing} title="Refresh">
          {refreshing ? <BusyIcon busy icon="ti-refresh" /> : <span className="tb-i tb-i-green">🔄</span>} Refresh
        </button>
      )}
      {onImport && (
        <button className="ca-btn" onClick={onImport} disabled={importDisabled} title="Import">
          <span className="tb-i tb-i-green">📥</span> Import
        </button>
      )}
      {onTemplate && (
        <button className="ca-btn" onClick={onTemplate} title="Template">
          <span className="tb-i tb-i-grey">⬇</span> Template
        </button>
      )}
      {beforeExport}
      {exportData ? (
        /* SHARED full-format Export to (all 10 ALPACA formats, ALL rows). */
        <ExportMenu filename={exportData.filename} headers={exportData.headers} rows={exportData.rows}
                    rowsProvider={exportData.rowsProvider} triggerClassName="ca-btn" />
      ) : exportOptions && exportOptions.length > 0 ? (
        <span className="app-toolbar__export">
          <button className="ca-btn" onClick={() => setExportMenuOpen(o => !o)} disabled={exportDisabled} title={exportLabel}>
            <span className="tb-i tb-i-teal">📤</span> {exportLabel} ▾
          </button>
          {exportMenuOpen && (
            <>
              <div className="app-toolbar__export-backdrop" onClick={() => setExportMenuOpen(false)} />
              <div className="app-toolbar__export-menu" role="menu">
                {exportOptions.map(opt => (
                  <button key={opt.label} className="app-toolbar__export-item" role="menuitem"
                          onClick={() => { setExportMenuOpen(false); opt.onSelect(); }}>
                    <i className={`ti ${opt.icon ?? 'ti-file-export'}`} /> {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </span>
      ) : onExport && (
        <button className="ca-btn" onClick={onExport} disabled={exportDisabled} title={exportLabel}>
          <span className="tb-i tb-i-teal">📤</span> {exportLabel}
        </button>
      )}
      {right}
    </div>
  );
}

/**
 * Thin vertical separator for grouping toolbar sections.  Drop between logical
 * button groups inside the `left` slot (e.g. Report · Save · actions · export).
 * Colour comes from the shared `--ai-toolbar-divider` theme token so theming
 * recolours it automatically — never hand-roll a per-form divider.
 */
export function ToolbarDivider() {
  return <span className="app-toolbar__divider" aria-hidden="true" />;
}
